/**
 * Returns true if a search hit has a deprecated status and should be moved to the end
 * of results. All other statuses preserve Algolia's original relevance ordering.
 */
export function isDeprecated(hit: { status?: { type?: string } }): boolean {
	return hit?.status?.type === 'deprecated';
}

/**
 * Moves deprecated hits to the end of the list while preserving the original
 * Algolia relevance order for all other results.
 */
export function sortHitsByStatus<T>(hits: T[]): T[] {
	// We cast to typedHits for the isDeprecated call, but filter on hits to preserve the T[] return type.
	// This avoids being coupled to Algolia typing, providing flexibility for future use.
	const typedHits = hits as Array<{ status?: { type?: string } }>;
	const nonDeprecated = hits.filter((_, i) => !isDeprecated(typedHits[i]));
	const deprecated = hits.filter((_, i) => isDeprecated(typedHits[i]));
	return [...nonDeprecated, ...deprecated];
}

const TRUNCATE_SIZE = 10;
const MAX_HITS_LIST = 9;

type HitNode = {
	isHighlighted: boolean;
	value: string;
};

export function formatAndTrimResult(parsedHit: HitNode[]): HitNode[] {
	const result = parsedHit.slice(0, MAX_HITS_LIST).map((node: HitNode, index: number) => {
		const { isHighlighted, value } = node;
		if (isHighlighted) {
			return node;
		}

		const sentences = value.split('.');

		// Don't truncate if the result is short.
		if (value.split(' ').length < TRUNCATE_SIZE) {
			return node;
		}

		// Keep the first sentence in the search result.
		if (index === 0 && parsedHit[index + 1]?.isHighlighted) {
			const lastSentenceBeforeHit = sentences[sentences.length - 1].trim();
			return {
				isHighlighted,
				value: `${lastSentenceBeforeHit} `,
			};
		}

		const firstSentenceAfterHit =
			sentences[0].length < TRUNCATE_SIZE && sentences.length > 1
				? `${sentences[0]}. ${sentences[1]}`
				: sentences[0];
		const firstSentenceBeforeNextHit = sentences[sentences.length - 1];

		return {
			isHighlighted,
			value:
				firstSentenceAfterHit +
				(firstSentenceBeforeNextHit
					? ` ... ${index === MAX_HITS_LIST - 1 ? '' : firstSentenceBeforeNextHit} `
					: '.'),
		};
	});

	// Add ellipsis to the sentence after the last hit
	const lastHit = result[result.length - 1];
	if (parsedHit.length > TRUNCATE_SIZE + 1 && !lastHit.value.trim().endsWith('...')) {
		lastHit.value = lastHit.value + ' ...';
	}

	return result;
}
